import React, { useMemo } from 'react';
import moment from 'moment';
import { Box, Button, Stack } from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useForm, useWatch } from 'react-hook-form';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { TabsOption } from 'global';
import { usePdf } from 'hooks/usePdf';
import { LenderPerformanceFilterFormValues, LenderPerformanceTab } from '../../../types';
import { LENDER_PERFORMANCE_DEFAULT_FILTERS } from '../../../constants';
import { mapFiltersToReportInput } from '../../../lib';
import { LenderPerformanceFilters } from './LenderPerformanceFilters';
import { ConversionFunnel } from './conversionFunnel';
import { ApprovalTerms } from './approvalTerms';
import { CardBackground } from 'components/CardBackground/CardBackground';
import makeStyles from '@mui/styles/makeStyles';
import { USE_PDF_SELECTOR } from 'hooks/usePdf';

export const LenderPerformance = () => {
  const classes = useStyles();
  const { contentRef, handleDownloadPdf } = usePdf();

  const { control, setValue } = useForm<LenderPerformanceFilterFormValues>({
    defaultValues: LENDER_PERFORMANCE_DEFAULT_FILTERS,
  });

  const filters = useWatch({ control }) as LenderPerformanceFilterFormValues;
  const input = useMemo(() => mapFiltersToReportInput(filters), [filters]);

  const handleRangeChange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('from', range.startDate);
    setValue('to', range.endDate);
  };

  const tabs: TabsOption<LenderPerformanceTab>[] = [
    {
      label: 'Conversion Funnel',
      value: LenderPerformanceTab.ConversionFunnel,
      component: <ConversionFunnel input={input} />,
    },
    {
      label: 'Approval Terms Analytics',
      value: LenderPerformanceTab.ApprovalTerms,
      component: <ApprovalTerms input={input} />,
    },
  ];

  return (
    <Stack ref={contentRef} gap="1rem">
      <Box data-pdf-exclude>
        <LenderPerformanceFilters control={control} onRangeChange={handleRangeChange} />
      </Box>

      <Box className={classes.pdfScope}>
        <TabsContainer<LenderPerformanceTab>
          tabs={tabs}
          initialTab={LenderPerformanceTab.ConversionFunnel}
          TabsLabelWrapper={({ children }) => (
            <Box data-pdf-exclude>
              <CardBackground>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  {children}

                  <Button variant="outlined" startIcon={<CloudDownloadOutlinedIcon />} onClick={handleDownloadPdf}>
                    Download PDF
                  </Button>
                </Stack>
              </CardBackground>
            </Box>
          )}
        />
      </Box>
    </Stack>
  );
};

const useStyles = makeStyles({
  pdfScope: {
    [`${USE_PDF_SELECTOR} &`]: {
      '& .tableContainer': { overflow: 'visible !important' },
      '& .tableContainer td': { padding: '4px 6px !important', fontSize: '11px !important' },
      '& .tableContainer th': { padding: '4px 6px !important', fontSize: '11px !important' },
    },
  },
});
